import { useMemo, useCallback } from 'react';
import type { TableRendererProps } from './table-renderer-props.ts';
import type { UseTableDataReturn } from './use-table-data.ts';

interface UseTableSelectionOptions {
  contract: TableRendererProps['contract'];
  state: UseTableDataReturn['state'];
  tableData: UseTableDataReturn['data'];
  getRowIdValue: (row: Record<string, unknown>) => string | undefined;
  actions: UseTableDataReturn['actions'];
}

export interface UseTableSelectionResult {
  selectionEnabled: boolean;
  allPageSelected: boolean;
  indeterminate: boolean;
  pageRowIds: string[];
  handleToggleRowSelection: (id: string) => void;
  handleSelectAll: (checked: boolean) => void;
  handleClearSelection: () => void;
}

export function useTableSelection({
  contract,
  state,
  tableData,
  getRowIdValue,
  actions,
}: UseTableSelectionOptions): UseTableSelectionResult {
  const selectionEnabled =
    (contract.selectionMode === 'single' || contract.selectionMode === 'multiple') &&
    contract.capabilities?.selection === true &&
    (contract.bulkActions?.length ?? 0) > 0;

  const pageRowIds = useMemo(
    () => tableData.map(getRowIdValue).filter((id): id is string => id !== undefined),
    [tableData, getRowIdValue],
  );

  const selectedSet = useMemo(() => new Set(state.selection.selectedIds), [state.selection.selectedIds]);
  const allPageSelected = useMemo(
    () => pageRowIds.length > 0 && pageRowIds.every((id) => selectedSet.has(id)),
    [pageRowIds, selectedSet],
  );
  const indeterminate = useMemo(
    () => !allPageSelected && pageRowIds.some((id) => selectedSet.has(id)),
    [pageRowIds, selectedSet, allPageSelected],
  );

  const handleToggleRowSelection = useCallback(
    (id: string) => {
      const current = state.selection.selectedIds;
      const alreadySelected = current.includes(id);
      const next = alreadySelected ? current.filter((x) => x !== id) : [...current, id];
      actions.setSelection(next, state.selection.selectAllMode);
    },
    [state.selection, actions],
  );

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (checked) {
        const next = [...new Set([...state.selection.selectedIds, ...pageRowIds])];
        actions.setSelection(next, 'page');
      } else {
        const next = state.selection.selectedIds.filter((id) => !pageRowIds.includes(id));
        actions.setSelection(next, 'page');
      }
    },
    [state.selection, pageRowIds, actions],
  );

  const handleClearSelection = useCallback(() => {
    actions.clearSelection();
  }, [actions]);

  return {
    selectionEnabled,
    allPageSelected,
    indeterminate,
    pageRowIds,
    handleToggleRowSelection,
    handleSelectAll,
    handleClearSelection,
  };
}
